import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { BaseAPIService, IEngineResponse } from '../base.service';
import { User } from './user.class';
import { MapObject } from '../../../shared/utilities/types.utilities';
import { toQueryString } from 'src/app/shared/utilities/api.utilities';
import { BaseDataClass } from '../base-api.class';

export interface ILoginOptions {
    /** URL to post login request to */
    url?: string;
    /** Whether form data is passed in the body of the request */
    form?: boolean;
    /** Custom headers to pass into the post request */
    headers?: MapObject<string>;
}

@Injectable({
    providedIn: 'root',
})
export class UsersService extends BaseAPIService<User> {
    constructor(protected http: HttpClient, protected location: Location) {
        super(http);
        this._name = 'Patient';
        this._api_route = 'patient';
        this._compare = (a, b) =>
            !(a.id || '').localeCompare(b.id) || !(a.email || '').localeCompare(b.email);
    }

    /** Currently logged in user */
    public get current(): User {
        return this.get('current_user');
    }

    public set current(user: User) {
        this.set('current_user', user);
    }

    public show(id: string, query_params?: MapObject) {
        let engine = false;
        let cache = 1000;
        if (query_params) {
            engine = !!query_params.engine;
            delete query_params.engine;
            cache = query_params.cache || 1000;
            delete query_params.cache;
        }
        const query = toQueryString(query_params);
        const key = `query|${query}`;
        if (!this._promises[key]) {
            this._promises[key] = new Promise((resolve, reject) => {
                const url = `${this.route(engine)}/${id}${query ? '?' + query : ''}`;
                let result: any[] = [];
                this.http.get(url).subscribe(
                    (d: IEngineResponse | MapObject[]) => {
                        result =
                            d && d instanceof Array
                                ? d.map((i) => this.process(i))
                                : d && !(d instanceof Array) && d.results
                                ? (d.results as MapObject[])
                                : [];
                    },
                    (e) => {
                        reject(e);
                        this._promises.new_item = null;
                    },
                    () => {
                        if (
                            (!query || (query_params && query_params.update_list)) &&
                            result.length > 0 &&
                            result[0] instanceof BaseDataClass
                        ) {
                            this.set('list', this.updateList(this.get('list'), result as User[]));
                        }
                        resolve(result);
                        this.timeout(key, () => (this._promises[key] = null), cache);
                    }
                );
            });
        }
        return this._promises[key];
    }

    public task(id: string, task_name: string, form_data: MapObject = {}): any {
        let engine = false;
        const query = toQueryString(form_data);
        const key = `task|${task_name}|${query}`;
        if (!this._promises[key]) {
            this._promises[key] = new Promise((resolve, reject) => {
                const post_data = { ...form_data, _task: task_name };
                const url = `${this.route(false)}/${task_name}`;
                let result = null;
                this.http.post(url, post_data).subscribe(
                    (d) => (result = d),
                    (e) => {
                        reject(e);
                        this.analyticsEvent(`${this._name.toLowerCase()}-task-${task_name}-failed`);
                        this._promises[key] = null;
                    },
                    () => {
                        resolve(result);
                        this.analyticsEvent(
                            `${this._name.toLowerCase()}-task-${task_name}-success`
                        );
                        this.timeout(key, () => (this._promises[key] = null), 1000);
                    }
                );
            });
        }
        return this._promises[key];
    }

    private _last_av_result: any = {};

    /**
     * Post results of the user's audio and video testing
     * @param audio_tested Result of the user's audio test
     * @param video_tested Result of the user's video test
     * @param new_chat Whether user has entered a new chat thread
     */
    public postAudioVideoResults(audio_tested: string, video_tested: string) {
        this._last_av_result = { audio_tested, video_tested };
        this.task('', 'av_results', this._last_av_result);
    }
    
    public setAudioVideoResults(audio_tested: string, video_tested: string) {
        this._last_av_result = { audio_tested, video_tested };
    }

    /** Post state that chat is empty */
    public postNewChat() {
        this.task('', 'av_results', { ...this._last_av_result, new_chat: true });
    }

    /**
     * Convert raw API data into a valid User Object
     * @param raw_item Raw API data
     */
    protected process(raw_data: any): User {
        return new User(this, raw_data);
    }
}
